import { useEffect, useState } from "react";

import { Home, Copy, Check, Folder, FolderKanban, MessageSquare, Layers } from "lucide-react";
import { useLocation, useParams } from "react-router-dom";

import { NavLink } from "@/components/NavLink";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage,
  BreadcrumbEllipsis,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "@/components/ui/sonner";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { supabase } from "@/integrations/supabase/client";
import { ROUTES } from "@/lib/routes";

interface BreadcrumbSegment {
  label: string;
  href?: string;
  type?: 'dashboard' | 'folder' | 'project' | 'session' | 'board' | 'other';
}

interface FolderHierarchy {
  id: string;
  name: string;
  parent_folder_id: string | null;
}

export const AppBreadcrumb = () => {
  const location = useLocation();
  const params = useParams();
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbSegment[]>([]);
  const [loading, setLoading] = useState(true);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  useEffect(() => {
    buildBreadcrumbs();
  }, [location.pathname, params.boardId, params.projectId, params.sessionId]);

  // Listen for project updates to refresh breadcrumb when project is moved
  useEffect(() => {
    if (!params.projectId) return;

    const channel = supabase
      .channel(`project-updates-${params.projectId}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "projects",
          filter: `id=eq.${params.projectId}`,
        },
        () => {
          buildBreadcrumbs();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [params.projectId]);

  const buildBreadcrumbs = async () => {
    setLoading(true);
    const segments: BreadcrumbSegment[] = [{ label: "Dashboard", href: ROUTES.DASHBOARD, type: 'dashboard' }];

    try {
      // Board detail page
      if (params.boardId && location.pathname.includes("/boards/")) {
        const { data: board } = await supabase
          .from("boards")
          .select("name")
          .eq("id", params.boardId)
          .single();

        if (board) {
          segments.push({ label: board.name, type: 'board' });
        }
      }

      // Project detail page
      if (params.projectId && location.pathname.includes("/projects/")) {
        const { data: project, error } = await supabase
          .from("projects")
          .select(`
            name,
            folder_id
          `)
          .eq("id", params.projectId)
          .single();

        if (error) {
          console.error("Breadcrumb: Error fetching project", error);
        }

        if (project) {
          // Add folder hierarchy if exists (folders are hierarchical)
          if (project.folder_id) {
            const folderHierarchy = await buildFolderHierarchy(project.folder_id);
            folderHierarchy.forEach((folder) => {
              segments.push({ label: folder.name, type: 'folder' });
            });
          }

          segments.push({ label: project.name, type: 'project' });
        }
      }

      // Session chat page
      if (params.sessionId && location.pathname.includes("/sessions/")) {
        const { data: session, error } = await supabase
          .from("sessions")
          .select(`
            name,
            project_id,
            projects(
              id,
              name,
              folder_id
            )
          `)
          .eq("id", params.sessionId)
          .single();

        if (error) {
          console.error("Breadcrumb: Error fetching session", error);
        }

        if (session && session.projects) {
          const project = session.projects;

          // Add folder hierarchy if exists (folders are hierarchical)
          if (project.folder_id) {
            const folderHierarchy = await buildFolderHierarchy(project.folder_id);
            folderHierarchy.forEach((folder) => {
              segments.push({ label: folder.name, type: 'folder' });
            });
          }

          // Add project
          segments.push({
            label: project.name,
            href: `/projects/${project.id}`,
            type: 'project'
          });

          // Add session
          segments.push({ label: session.name, type: 'session' });
        }
      }

      // Projects page
      if (location.pathname === "/projects") {
        segments.push({ label: "All Projects", type: 'other' });
      }

      // Admin pages
      if (location.pathname.startsWith("/admin")) {
        segments.push({ label: "Admin Panel", type: 'other' });
      }

      // Search page
      if (location.pathname === "/search") {
        segments.push({ label: "Search", type: 'other' });
      }
    } catch (error) {
      console.error("Error building breadcrumbs:", error);
    } finally {
      setBreadcrumbs(segments);
      setLoading(false);
    }
  };

  const buildFolderHierarchy = async (folderId: string): Promise<FolderHierarchy[]> => {
    const hierarchy: FolderHierarchy[] = [];
    let currentFolderId: string | null = folderId;

    while (currentFolderId) {
      const { data: folder } = await supabase
        .from("folders")
        .select("id, name, parent_folder_id")
        .eq("id", currentFolderId)
        .single();

      if (folder) {
        hierarchy.unshift(folder);
        currentFolderId = folder.parent_folder_id;
      } else {
        break;
      }
    }

    return hierarchy;
  };

  // Don't show breadcrumbs on auth pages
  if (
    location.pathname === "/" ||
    location.pathname.startsWith("/auth") ||
    location.pathname.startsWith("/waitlist") ||
    location.pathname.startsWith("/verify-email") ||
    location.pathname.startsWith("/verify-magic-link") ||
    location.pathname.startsWith("/reset-password")
  ) {
    return null;
  }

  // Show skeleton while loading
  if (loading) {
    return (
      <div className="border-b bg-muted/30">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center gap-2">
            <Skeleton className="h-4 w-20" />
            <span className="text-muted-foreground">/</span>
            <Skeleton className="h-4 w-32" />
            <span className="text-muted-foreground">/</span>
            <Skeleton className="h-4 w-24" />
          </div>
        </div>
      </div>
    );
  }

  // Don't show if only showing dashboard
  if (breadcrumbs.length <= 1) {
    return null;
  }

  const truncateText = (text: string, maxLength: number = 30) => {
    return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
  };

  const copyToClipboard = (text: string, index: number) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const getIconForType = (type?: string) => {
    switch (type) {
      case 'dashboard':
        return <Home className="h-3.5 w-3.5" />;
      case 'folder':
        return <Folder className="h-3.5 w-3.5" />;
      case 'project':
        return <FolderKanban className="h-3.5 w-3.5" />;
      case 'session':
        return <MessageSquare className="h-3.5 w-3.5" />;
      case 'board':
        return <Layers className="h-3.5 w-3.5" />;
      default:
        return null;
    }
  };

  const renderBreadcrumbSegment = (segment: BreadcrumbSegment, index: number, isLast: boolean) => {
    const isTruncated = segment.label.length > 30;
    const displayLabel = truncateText(segment.label);
    const icon = getIconForType(segment.type);

    const content = (
      <div className="flex items-center gap-1 group">
        {isTruncated ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-1.5">
                {icon}
                {isLast ? (
                  <BreadcrumbPage className="max-w-[200px] truncate">
                    {displayLabel}
                  </BreadcrumbPage>
                ) : segment.href ? (
                  <BreadcrumbLink asChild>
                    <NavLink to={segment.href} className="max-w-[200px] truncate">
                      {displayLabel}
                    </NavLink>
                  </BreadcrumbLink>
                ) : (
                  <span className="text-muted-foreground max-w-[200px] truncate">
                    {displayLabel}
                  </span>
                )}
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p className="max-w-xs">{segment.label}</p>
            </TooltipContent>
          </Tooltip>
        ) : (
          <div className="flex items-center gap-1.5">
            {icon}
            {isLast ? (
              <BreadcrumbPage>{segment.label}</BreadcrumbPage>
            ) : segment.href ? (
              <BreadcrumbLink asChild>
                <NavLink to={segment.href}>{segment.label}</NavLink>
              </BreadcrumbLink>
            ) : (
              <span className="text-muted-foreground">{segment.label}</span>
            )}
          </div>
        )}
        {index !== 0 && (
          <Button
            variant="ghost"
            size="icon"
            className="h-4 w-4 p-0 opacity-0 group-hover:opacity-100 transition-opacity ml-1"
            onClick={(e) => {
              e.preventDefault();
              copyToClipboard(segment.label, index);
            }}
          >
            {copiedIndex === index ? (
              <Check className="h-3 w-3 text-green-600" />
            ) : (
              <Copy className="h-3 w-3" />
            )}
          </Button>
        )}
      </div>
    );

    return content;
  };

  const renderBreadcrumbs = () => {
    // Collapse breadcrumbs if more than 4 segments (show first, dropdown for middle, last)
    if (breadcrumbs.length > 4) {
      const first = breadcrumbs[0];
      const middle = breadcrumbs.slice(1, -1);
      const last = breadcrumbs[breadcrumbs.length - 1];

      return (
        <>
          <BreadcrumbItem>
            {renderBreadcrumbSegment(first, 0, false)}
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          
          <BreadcrumbItem>
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1">
                <BreadcrumbEllipsis className="h-4 w-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="bg-popover z-50">
                {middle.map((segment, idx) => {
                  const actualIndex = idx + 1;
                  const icon = getIconForType(segment.type);
                  return (
                    <DropdownMenuItem key={actualIndex} asChild>
                      {segment.href ? (
                        <NavLink 
                          to={segment.href}
                          className="flex items-center justify-between w-full cursor-pointer"
                        >
                          <span className="flex items-center gap-1.5 flex-1">
                            {icon}
                            {segment.label}
                          </span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-4 w-4 p-0 ml-2"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              copyToClipboard(segment.label, actualIndex);
                            }}
                          >
                            {copiedIndex === actualIndex ? (
                              <Check className="h-3 w-3 text-green-600" />
                            ) : (
                              <Copy className="h-3 w-3" />
                            )}
                          </Button>
                        </NavLink>
                      ) : (
                        <div className="flex items-center justify-between w-full">
                          <span className="flex items-center gap-1.5 flex-1 text-muted-foreground">
                            {icon}
                            {segment.label}
                          </span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-4 w-4 p-0 ml-2"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              copyToClipboard(segment.label, actualIndex);
                            }}
                          >
                            {copiedIndex === actualIndex ? (
                              <Check className="h-3 w-3 text-green-600" />
                            ) : (
                              <Copy className="h-3 w-3" />
                            )}
                          </Button>
                        </div>
                      )}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          
          <BreadcrumbItem>
            {renderBreadcrumbSegment(last, breadcrumbs.length - 1, true)}
          </BreadcrumbItem>
        </>
      );
    }

    // Normal display for shorter breadcrumbs
    return breadcrumbs.map((segment, index) => {
      const isLast = index === breadcrumbs.length - 1;
      return (
        <div key={index} className="flex items-center">
          <BreadcrumbItem>
            {renderBreadcrumbSegment(segment, index, isLast)}
          </BreadcrumbItem>
          {!isLast && <BreadcrumbSeparator />}
        </div>
      );
    });
  };

  return (
    <div className="sticky top-0 z-40 border-b bg-background/95 backdrop-blur-sm supports-backdrop-filter:bg-background/60">
      <div className="container mx-auto px-4 py-3">
        <TooltipProvider>
          <Breadcrumb>
            <BreadcrumbList>
              {renderBreadcrumbs()}
            </BreadcrumbList>
          </Breadcrumb>
        </TooltipProvider>
      </div>
    </div>
  );
};
